import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MicrosoftTab from "@/components/settings/MicrosoftTab";
import EmailRoutingTab from "@/components/settings/EmailRoutingTab";
import BrandingTab from "@/components/settings/BrandingTab";
import UsersTab from "@/components/settings/UsersTab";
import GuestsTab from "@/components/settings/GuestsTab";
import GeneralTab from "@/components/settings/GeneralTab";
import MarketingChoicesTab from "@/components/settings/MarketingChoicesTab";
import IntegrationsTab from "@/components/settings/IntegrationsTab";
import LostReasonsTab from "@/components/settings/LostReasonsTab";
import SlaTab from "@/components/settings/SlaTab";
import SourcesTab from "@/components/settings/SourcesTab";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem("bi_settings_tab") || "general");

  const changeTab = (v) => {
    setActiveTab(v);
    sessionStorage.setItem("bi_settings_tab", v);
  };

  const fetchSettings = async () => {
    // Only block the page on the very first load; refetches after a tab's
    // Save must not unmount the tabs (that reset the user back to General).
    if (!settings) setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const { data } = await api.get("/users");
      setPendingCount(data.filter((u) => u.status === "pending").length);
    } catch {
      setPendingCount(0);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchPendingCount();
  }, []);

  if ((loading && !settings) || !settings) return <div data-testid="settings-loading" className="text-sm text-slate-500">Loading…</div>;

  const tabClass =
    "data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none px-0 pb-3 label-tracking !text-xs data-[state=active]:text-slate-900 text-slate-500";

  return (
    <div data-testid="settings-page" className="space-y-8">
      <div>
        <div className="label-tracking">Configuration</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none mt-2 text-slate-900">
          Settings.
        </h1>
        <p className="text-sm text-slate-500 mt-3">Manage integrations, automation, users, and email routing.</p>
      </div>

      <Tabs value={activeTab} onValueChange={changeTab} className="w-full">
        <TabsList data-testid="settings-tabs" className="bg-transparent border-b border-slate-200 rounded-none h-auto p-0 gap-6 justify-start flex-wrap">
          <TabsTrigger value="general" data-testid="tab-general" className={tabClass}>
            General
          </TabsTrigger>
          <TabsTrigger value="microsoft" data-testid="tab-microsoft" className={tabClass}>
            Microsoft 365
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations" className={tabClass}>
            Integrations
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className={`${tabClass} relative`}>
            Users &amp; Roles
            {pendingCount > 0 && (
              <span
                data-testid="pending-badge"
                className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[0.6rem] font-semibold"
              >
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="guests" data-testid="tab-guests" className={tabClass}>
            Guests
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email" className={tabClass}>
            Email Routing
          </TabsTrigger>
          <TabsTrigger value="lost-reasons" data-testid="tab-lost-reasons" className={tabClass}>
            Lost Reasons
          </TabsTrigger>
          <TabsTrigger value="sources" data-testid="tab-sources" className={tabClass}>
            Sources
          </TabsTrigger>
          <TabsTrigger value="sla" data-testid="tab-sla" className={tabClass}>
            SLA
          </TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing" className={tabClass}>
            Marketing
          </TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding" className={tabClass}>
            Branding
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-8">
          <GeneralTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>

        <TabsContent value="microsoft" className="mt-8">
          <MicrosoftTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>

        <TabsContent value="integrations" className="mt-8">
          <IntegrationsTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>

        <TabsContent value="users" className="mt-8">
          <UsersTab onChanged={fetchPendingCount} />
        </TabsContent>

        <TabsContent value="guests" className="mt-8">
          <GuestsTab />
        </TabsContent>

        <TabsContent value="email" className="mt-8">
          <EmailRoutingTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>

        <TabsContent value="lost-reasons" className="mt-8">
          <LostReasonsTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>

        <TabsContent value="sources" className="mt-8">
          <SourcesTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>

        <TabsContent value="sla" className="mt-8">
          <SlaTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>

        <TabsContent value="marketing" className="mt-8">
          <MarketingChoicesTab />
        </TabsContent>

        <TabsContent value="branding" className="mt-8">
          <BrandingTab settings={settings} onSaved={fetchSettings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
